/*
  <loop-mega-menu> — the desktop panel behind each "Mega menu item" block in
  sections/header.liquid ("Mega menu (Loop)" mode). Loaded with defer, only in that mode.

  A port of loopearplugs.com's MegaMenu element (read from their minified bundle on 18 OCT 2026):
  - hovering the top-level <li> opens its panel; leaving the <li>, or leaving the white panel
    for the dark overlay, closes it;
  - the visually-hidden disclosure button opens it for keyboard users and traps focus inside it;
  - focus landing on another top-level item closes every panel;
  - an open panel drops `inert`, the button reports aria-expanded, and the body gets a class.

  What changed from Loop's code, and why:
  - Loop tests a `touch` class on <html>; this theme never sets one, so "touch" is read from
    (hover: hover) and (pointer: fine) at the moment of each event;
  - Loop's focus helpers become this theme's globals getFocusableElements / trapFocus /
    removeTrapFocus, which live in the remote main.js, so every call is behind a typeof guard;
  - Escape is handled on the document, and returns focus to the trigger of the open panel;
  - opening one panel closes any other, and a click outside the open item, the pageshow event
    (back/forward cache) and crossing the 990px breakpoint all close everything — so the search
    modal or the cart drawer never opens underneath a panel;
  - the body class is lmm-open (Loop: mega-menu-open), and the trigger toggles: a second press
    on a panel it opened closes it again;
  - Loop binds its nav focusin once per item, and every other item then closes the open panel
    even when focus lands on that panel's own label; here one listener per nav closes only when
    focus reaches a different item;
  - Loop closes when the pointer leaves the white panel in any direction, including back up onto
    the item's own label, where the panel then stays shut until the pointer leaves the item and
    comes back; here going back up to the label keeps it open;
  - the outside-click listener runs in the capture phase, so a keyboard-opened panel lets go of
    its focus trap before the search modal (which traps focus synchronously in its own click
    handler) takes one; in the bubble phase removeTrapFocus() would strip the modal's trap;
  - on touch screens wide enough for this nav (tablets in landscape) there is no hover and the
    disclosure button is visually hidden, so the first tap on a label opens its panel and a
    second tap follows the link;
  - a panel opened from the keyboard stays in keyboard mode when the pointer wanders over it,
    and whatever closes a panel that holds focus hands focus back to its trigger (Loop's
    closeMegaMenu does the same with removeTrapFocus(trigger));
  - focus leaving the item for the icons or the page closes it, which Loop leaves to its trap;
  - in the theme editor a panel opened by selecting a block stays pinned until the block is
    deselected, so moving the pointer to the settings sidebar does not close it.
*/
(() => {
  if (customElements.get('loop-mega-menu')) return;

  const DESKTOP_QUERY = '(min-width: 990px)';
  const isTouch = () => !window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  const focusableIn = (container) =>
    typeof getFocusableElements === 'function'
      ? getFocusableElements(container)
      : Array.from(container.querySelectorAll('a[href], button:enabled, [tabindex]:not([tabindex^="-"])'));

  // Focus reaching another top-level item closes the open panel. Focus inside an open panel, or
  // on the open item's own label and trigger, leaves it alone.
  const boundNavs = new WeakSet();
  const onNavFocusIn = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || target.closest('.lmm-overlay.open')) return;
    const item = target.closest('li');
    if (!item || !event.currentTarget.contains(item)) return;
    if (item.querySelector('.lmm-overlay.open')) return;
    LoopMegaMenu.closeAll();
  };

  class LoopMegaMenu extends HTMLElement {
    constructor() {
      super();
      this.boundCloseOnLeave = this.closeMegaMenu.bind(this);
      this.expanded = false;
      this.hover = false;
      this.trapped = false;
    }

    connectedCallback() {
      if (this.initialised) return;

      this.parent = this.closest('li');
      this.label = this.querySelector('.lmm-item__link');
      this.trigger = this.querySelector('.lmm-disclosure');
      this.overlay = this.querySelector('.lmm-overlay');
      this.wrapper = this.querySelector('.lmm-wrapper');
      this.nav = this.closest('nav');
      if (!this.parent || !this.trigger || !this.overlay || !this.wrapper) return;
      this.initialised = true;

      if (this.nav && !boundNavs.has(this.nav)) {
        boundNavs.add(this.nav);
        this.nav.addEventListener('focusin', onNavFocusIn);
      }

      // Loop closes on an overlay click only for touch; a mouse never gets there with a panel
      // it opened by hover. Closing for every pointer also covers a panel opened by keyboard.
      this.overlay.addEventListener('click', (event) => {
        if (event.target.closest('.lmm-wrapper')) return;
        LoopMegaMenu.closeAll();
      });

      this.wrapper.addEventListener('mouseleave', (event) => {
        if (isTouch() || LoopMegaMenu.pinned || (this.expanded && !this.hover)) return;
        // Back up onto this item's label (or its padding): the <li> mouseleave closes it later.
        const to = event.relatedTarget;
        if (to instanceof Node && this.parent.contains(to) && !this.overlay.contains(to)) return;
        LoopMegaMenu.closeAll();
      });

      this.parent.addEventListener('mouseenter', (event) => {
        if (isTouch() || LoopMegaMenu.pinned || (this.expanded && !this.hover)) return;
        event.stopPropagation();
        this.hover = true;
        this.openMegaMenu();
      });

      // Touch: first tap on the label opens the panel, a second tap (panel open) follows the link.
      this.label?.addEventListener('click', (event) => {
        if (!isTouch() || this.expanded) return;
        event.preventDefault();
        this.hover = false;
        this.openMegaMenu({ trap: false });
      });

      // Focus leaving this item (for the header icons or the page) closes its panel. A null
      // relatedTarget (window blur, a click on nothing focusable) is left to the click handlers.
      this.parent.addEventListener('focusout', (event) => {
        const to = event.relatedTarget;
        if (!this.expanded || LoopMegaMenu.pinned || !(to instanceof Node) || this.parent.contains(to)) return;
        this.close();
      });

      this.trigger.addEventListener('click', (event) => {
        event.stopPropagation();
        event.preventDefault();
        if (this.expanded && !this.hover) {
          this.close(true);
          return;
        }
        this.hover = false;
        this.openMegaMenu();
      });
    }

    disconnectedCallback() {
      if (this.expanded) this.close();
    }

    openMegaMenu({ trap = true } = {}) {
      LoopMegaMenu.closeAll(this);

      this.overlay.classList.add('open');
      document.body.classList.add('lmm-open');
      this.overlay.removeAttribute('inert');
      this.trigger.setAttribute('aria-expanded', 'true');
      this.expanded = true;

      if (this.hover) {
        this.parent.addEventListener('mouseleave', this.boundCloseOnLeave, { once: true });
        return;
      }

      if (!trap) return;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!this.expanded) return;
          const target = focusableIn(this.overlay)[0] || this.trigger;
          if (typeof trapFocus === 'function') {
            trapFocus(this.overlay, target);
            this.trapped = true;
          } else {
            target.focus();
          }
        });
      });
    }

    // Loop's closeMegaMenu: the hover path (mouseleave on the <li>).
    closeMegaMenu() {
      if (LoopMegaMenu.pinned) return;
      this.close();
    }

    close(focusTrigger = false) {
      if (!this.overlay) return;
      // Inert on a panel that holds focus would drop focus onto <body>; it goes to the trigger.
      const hadFocus = this.overlay.contains(document.activeElement);
      this.overlay.classList.remove('open');
      this.overlay.setAttribute('inert', '');
      this.trigger.setAttribute('aria-expanded', 'false');
      this.expanded = false;
      this.parent.removeEventListener('mouseleave', this.boundCloseOnLeave);

      if (this.trapped) {
        this.trapped = false;
        if (typeof removeTrapFocus === 'function') removeTrapFocus();
      }
      if (!document.querySelector('.lmm-overlay.open')) document.body.classList.remove('lmm-open');
      if (focusTrigger || hadFocus) this.trigger.focus();
    }

    static closeAll(except = null) {
      document.querySelectorAll('loop-mega-menu').forEach((menu) => {
        if (menu !== except && menu.expanded) menu.close();
      });
      if (!document.querySelector('.lmm-overlay.open')) document.body.classList.remove('lmm-open');
    }
  }

  customElements.define('loop-mega-menu', LoopMegaMenu);

  document.addEventListener('keydown', (event) => {
    if (event.code !== 'Escape' && event.key !== 'Escape') return;
    const open = document.querySelector('.lmm-overlay.open')?.closest('loop-mega-menu');
    if (!open) return;
    LoopMegaMenu.closeAll();
    open.trigger?.focus();
  });

  document.addEventListener(
    'click',
    (event) => {
      const open = document.querySelector('.lmm-overlay.open')?.closest('loop-mega-menu');
      if (!open || open.contains(event.target)) return;
      LoopMegaMenu.closeAll();
    },
    true
  );

  window.addEventListener('pageshow', () => LoopMegaMenu.closeAll());
  window.matchMedia(DESKTOP_QUERY).addEventListener('change', () => LoopMegaMenu.closeAll());

  // Theme editor: selecting an item, a column or a card shows its panel (without trapping
  // focus, which would fight the editor) and pins it, so the pointer leaving the preview for
  // the settings sidebar does not close it; deselecting unpins and hides it again.
  LoopMegaMenu.pinned = null;
  document.addEventListener('shopify:block:select', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const menu = target.closest('loop-mega-menu') || target.querySelector('loop-mega-menu');
    if (!menu || typeof menu.openMegaMenu !== 'function') return;
    LoopMegaMenu.pinned = null;
    menu.parent?.removeEventListener('mouseleave', menu.boundCloseOnLeave);
    menu.hover = false;
    menu.openMegaMenu({ trap: false });
    LoopMegaMenu.pinned = menu;
  });
  const unpin = () => {
    LoopMegaMenu.pinned = null;
    LoopMegaMenu.closeAll();
  };
  document.addEventListener('shopify:block:deselect', unpin);
  document.addEventListener('shopify:section:unload', unpin);
})();
